function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { Ribbon } from "../../components/analytics-kit";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import Auth from "../../functions/authenticate";
import ServiceFactory from "../../service";
import { Button } from "@progress/kendo-react-buttons";

/*
 * Accessibility checks (/Ada, kind "ada") and SEO checks (/Seo, kind "seo").
 * One component for both; the backend is MarketResearchService /v1/{ada|seo}/*.
 * Design: PlunkDocs mockups/apps-redesign-2026-10-09.html boards 7 and 8.
 *
 * Issues come back one row per (check, page, element). The page groups them
 * by check so each problem shows once, with the pages it was found on.
 * Pages that differ only by query string or hash count as one page.
 */

function svcRoot(kind, api) {
  // /v1/{kind}/... rides the "product" endpoint type (path /v1)
  const Service = ServiceFactory.getType(`/${kind}${api}`, "product");
  return new Service();
}
async function safe(promise, fallback) {
  try {
    return (await promise) || fallback;
  } catch (e) {
    return fallback;
  }
}
function postAsync(kind, api) {
  return new Promise(resolve => {
    svcRoot(kind, api).post({}, (err, data) => resolve({
      err,
      data
    }));
  });
}
function toDate(d) {
  if (!d) return null;
  const x = new Date(d);
  return isNaN(x.getTime()) ? null : x;
}
function fmtDay(d) {
  const x = toDate(d);
  if (!x) return "";
  const now = new Date();
  const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(x, now)) return "today";
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (sameDay(x, y)) return "yesterday";
  return `on ${x.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric"
  })}`;
}
function fmtDate(d) {
  const x = toDate(d);
  return x ? x.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: x.getFullYear() === new Date().getFullYear() ? undefined : "numeric"
  }) : "";
}
function fmtWhen(d) {
  const x = toDate(d);
  return x ? `${fmtDate(x)}, ${x.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit"
  })}` : "";
}
function plural(n, one, many) {
  return `${n} ${n === 1 ? one : many || `${one}s`}`;
}

// One page = the URL without its query string or hash (and without a trailing slash).
function pageKey(url) {
  const s = String(url || "").split("#")[0].split("?")[0];
  return s.length > 1 && /[^/]\/$/.test(s) && !/:\/\/[^/]+\/$/.test(s) ? s.slice(0, -1) : s;
}
function siteName(site) {
  if (!site) return "your site";
  try {
    return new URL(site.url).hostname.replace(/^www\./, "");
  } catch (e) {
    return site.label || site.url;
  }
}
function humanize(id) {
  const s = String(id || "").replace(/[-_]+/g, " ").trim();
  return s ? s[0].toUpperCase() + s.slice(1) : "Other problem";
}
const SEV_RANK = {
  critical: 0,
  serious: 1,
  high: 1,
  moderate: 2,
  medium: 2,
  minor: 3,
  low: 3
};
const SEV_TONE = {
  critical: "red",
  serious: "red",
  high: "red",
  moderate: "amber",
  medium: "amber",
  minor: "blue",
  low: "blue"
};
function sevRank(s) {
  const r = SEV_RANK[String(s || "").toLowerCase()];
  return r == null ? 4 : r;
}

// Plain-language names for accessibility checks (axe rule ids).
const ADA_RULES = {
  "color-contrast": {
    t: "Text is too light to read against its background",
    w: "People with low vision can't read it.",
    f: "make the text darker or the background lighter (contrast at least 4.5 to 1).",
    big: "text people can't read easily"
  },
  "color-contrast-enhanced": {
    t: "Text could stand out more from its background",
    w: "Some people with low vision still struggle to read it.",
    f: "raise the contrast between text and background (at least 7 to 1 for full marks)."
  },
  "image-alt": {
    t: "Images have no description",
    w: "Screen readers can't tell people what the image shows.",
    f: "add a short description (alt text) to each image, or mark purely decorative images as decorative.",
    big: "images with no description"
  },
  "input-image-alt": {
    t: "Image buttons have no description",
    w: "Screen reader users can't tell what the button does.",
    f: "add alt text that says what the button does."
  },
  "image-redundant-alt": {
    t: "Image descriptions repeat the nearby text",
    w: "Screen readers read the same words twice.",
    f: "shorten the alt text or leave it empty when the text next to the image already says it."
  },
  "svg-img-alt": {
    t: "Graphics have no description",
    w: "Screen readers can't tell people what the graphic shows.",
    f: "add a title or label to each meaningful graphic."
  },
  "role-img-alt": {
    t: "Graphics have no description",
    w: "Screen readers can't tell people what the graphic shows.",
    f: "add a label to each element shown as an image."
  },
  "object-alt": {
    t: "Embedded items have no description",
    w: "Screen readers can't say what the embedded item is.",
    f: "add a short text alternative to each embedded item."
  },
  "area-alt": {
    t: "Clickable image areas have no description",
    w: "Screen reader users can't tell where each area leads.",
    f: "add alt text to each clickable area of the image."
  },
  "button-name": {
    t: "Buttons with only an icon have no name",
    w: "Screen readers announce them as just “button”.",
    f: "add a short label to each icon button.",
    big: "buttons with no name"
  },
  "link-name": {
    t: "Links have no text",
    w: "Screen readers announce them as just “link”, so people can't tell where they go.",
    f: "give each link visible text or a label, especially links that are only an icon or image.",
    big: "links with no text"
  },
  "link-in-text-block": {
    t: "Links only stand out by color",
    w: "People who can't see color differences can't find the links.",
    f: "underline links inside text, or add another visual cue besides color."
  },
  "identical-links-same-purpose": {
    t: "Links with the same text go to different places",
    w: "People can't tell the links apart.",
    f: "make link text say where each link goes."
  },
  "label": {
    t: "Form fields have no label",
    w: "People using screen readers don't know what to type.",
    f: "add a visible label to each field and connect it to the field.",
    big: "form fields with no label"
  },
  "label-title-only": {
    t: "Form fields are only labeled by a tooltip",
    w: "The hint disappears and many people never see it.",
    f: "add a visible label to each field."
  },
  "form-field-multiple-labels": {
    t: "Form fields have more than one label",
    w: "Screen readers may read the wrong one.",
    f: "keep one label per field."
  },
  "select-name": {
    t: "Drop-down menus have no label",
    w: "People using screen readers don't know what the menu is for.",
    f: "add a label to each drop-down menu."
  },
  "autocomplete-valid": {
    t: "Form fields have the wrong autofill setting",
    w: "Browsers can't fill in details for people who need help typing.",
    f: "use the standard autofill values (like email or postal code) on each field."
  },
  "html-has-lang": {
    t: "Pages don't say what language they're in",
    w: "Screen readers may read the page with the wrong accent or rules.",
    f: "set the page language once in the site template (for example lang=\"en\")."
  },
  "html-lang-valid": {
    t: "Page language setting isn't valid",
    w: "Screen readers may read the page with the wrong accent or rules.",
    f: "use a standard language code (for example \"en\" or \"fr\")."
  },
  "valid-lang": {
    t: "Language settings in the page aren't valid",
    w: "Screen readers may read those parts with the wrong accent.",
    f: "use a standard language code for each part in another language."
  },
  "document-title": {
    t: "Pages have no title",
    w: "People can't tell browser tabs apart or know where they landed.",
    f: "give each page a short title that says what it is."
  },
  "heading-order": {
    t: "Headings skip levels",
    w: "People who navigate by headings lose track of how the page is organized.",
    f: "use headings in order (a main heading, then subheadings) without skipping levels."
  },
  "page-has-heading-one": {
    t: "Pages have no main heading",
    w: "People who navigate by headings can't find where the content starts.",
    f: "add one main heading at the top of each page's content."
  },
  "empty-heading": {
    t: "Headings are empty",
    w: "Screen readers announce a heading with nothing in it.",
    f: "add text to each heading or remove the empty ones."
  },
  "region": {
    t: "Some content sits outside the page's main areas",
    w: "People who jump between page areas can miss it.",
    f: "place all content inside the page's header, main area, or footer."
  },
  "bypass": {
    t: "No way to skip past the menu",
    w: "Keyboard users have to tab through every menu link on every page.",
    f: "add a “Skip to content” link or mark the main area of the page."
  },
  "skip-link": {
    t: "The skip link goes nowhere",
    w: "Keyboard users press it and nothing happens.",
    f: "point the skip link at the start of the main content."
  },
  "list": {
    t: "Lists are built incorrectly",
    w: "Screen readers can't say how many items a list has.",
    f: "put only list items directly inside each list."
  },
  "listitem": {
    t: "List items sit outside a list",
    w: "Screen readers can't tell they belong together.",
    f: "wrap list items in a list."
  },
  "definition-list": {
    t: "Definition lists are built incorrectly",
    w: "Screen readers can't pair terms with their meanings.",
    f: "keep only terms and their descriptions inside each definition list."
  },
  "dlitem": {
    t: "Definition items sit outside a definition list",
    w: "Screen readers can't pair terms with their meanings.",
    f: "wrap terms and descriptions in a definition list."
  },
  "frame-title": {
    t: "Embedded frames have no title",
    w: "Screen reader users can't tell what an embedded video, map, or widget is.",
    f: "add a short title to each embedded frame."
  },
  "frame-focusable-content": {
    t: "Embedded frames can't be reached by keyboard",
    w: "Keyboard users can't use what's inside them.",
    f: "let keyboard users tab into frames that have controls."
  },
  "duplicate-id": {
    t: "Page elements share the same ID",
    w: "Assistive tools can point at the wrong element.",
    f: "give each element on a page its own unique ID."
  },
  "duplicate-id-aria": {
    t: "Labeled elements share the same ID",
    w: "Screen readers can read the wrong label.",
    f: "give each labeled element its own unique ID."
  },
  "duplicate-id-active": {
    t: "Buttons or links share the same ID",
    w: "Keyboard and screen reader users can land on the wrong control.",
    f: "give each control its own unique ID."
  },
  "meta-viewport": {
    t: "Pages block zooming in",
    w: "People with low vision can't enlarge the page on their phone.",
    f: "remove the setting that turns off pinch-to-zoom."
  },
  "meta-refresh": {
    t: "Pages reload or redirect on a timer",
    w: "People lose their place before they finish reading.",
    f: "remove timed refreshes or let people control them."
  },
  "nested-interactive": {
    t: "Controls are placed inside other controls",
    w: "Screen readers and keyboards can't reach the inner control.",
    f: "keep buttons and links separate rather than one inside another."
  },
  "scrollable-region-focusable": {
    t: "Scrolling areas can't be reached by keyboard",
    w: "Keyboard users can't scroll to see the content.",
    f: "let keyboard users tab into each scrolling area."
  },
  "tabindex": {
    t: "Custom tab order is set",
    w: "Keyboard users jump around the page in a confusing order.",
    f: "remove positive tab order values so the page follows its natural order."
  },
  "target-size": {
    t: "Tap targets are too small",
    w: "People with limited hand control miss them.",
    f: "make buttons and links at least 24 by 24 pixels, with space around them."
  },
  "video-caption": {
    t: "Videos have no captions",
    w: "People who are deaf or hard of hearing miss what's said.",
    f: "add captions to each video."
  },
  "td-headers-attr": {
    t: "Table cells point to the wrong headers",
    w: "Screen readers read the wrong column or row name.",
    f: "connect each cell to the right table header."
  },
  "th-has-data-cells": {
    t: "Table headers have no data under them",
    w: "Screen readers announce headers that describe nothing.",
    f: "remove empty headers or fill in the cells they describe."
  },
  "empty-table-header": {
    t: "Table headers are empty",
    w: "Screen readers can't name the column or row.",
    f: "add text to each table header."
  },
  "presentation-role-conflict": {
    t: "Decorative items can still be focused",
    w: "Screen readers get confused about whether to read them.",
    f: "remove focus and labels from purely decorative items."
  },
  "blink": {
    t: "Text blinks",
    w: "Blinking text is hard to read and can be distracting.",
    f: "stop the text from blinking."
  },
  "marquee": {
    t: "Text scrolls on its own",
    w: "Moving text is hard to read and can't be paused.",
    f: "replace scrolling text with static text."
  },
  "aria-allowed-attr": {
    t: "Elements use settings that don't apply to them",
    w: "Screen readers may describe them wrongly.",
    f: "remove accessibility settings that don't fit the element."
  },
  "aria-allowed-role": {
    t: "Elements are given a role that doesn't fit",
    w: "Screen readers describe the element as something it isn't.",
    f: "remove or correct the role on each element."
  },
  "aria-hidden-focus": {
    t: "Hidden elements can still be reached by keyboard",
    w: "Keyboard users land on things screen readers say aren't there.",
    f: "stop hidden elements from taking keyboard focus."
  },
  "aria-hidden-body": {
    t: "The whole page is hidden from screen readers",
    w: "Screen reader users hear nothing.",
    f: "remove the hidden setting from the page body."
  },
  "aria-required-attr": {
    t: "Interactive parts are missing required settings",
    w: "Screen readers can't say the state of things like toggles or sliders.",
    f: "add the settings each control needs (like checked or expanded)."
  },
  "aria-required-children": {
    t: "Menus or lists are missing their items",
    w: "Screen readers can't describe the structure.",
    f: "put the expected items inside each menu, list, or tab group."
  },
  "aria-required-parent": {
    t: "Items sit outside their menu or list",
    w: "Screen readers can't tell what group they belong to.",
    f: "place each item inside the group it belongs to."
  },
  "aria-roles": {
    t: "Elements use roles that don't exist",
    w: "Screen readers ignore or misread them.",
    f: "use a standard role or remove it."
  },
  "aria-valid-attr": {
    t: "Elements use misspelled accessibility settings",
    w: "Screen readers ignore them.",
    f: "fix the spelling of each setting."
  },
  "aria-valid-attr-value": {
    t: "Accessibility settings have invalid values",
    w: "Screen readers can describe the element wrongly.",
    f: "correct the value of each setting."
  },
  "aria-prohibited-attr": {
    t: "Elements carry labels that aren't allowed on them",
    w: "Screen readers may skip the label.",
    f: "move the label to an element that supports it, or use visible text."
  },
  "aria-command-name": {
    t: "Custom buttons have no name",
    w: "Screen readers can't say what they do.",
    f: "add a short label to each custom button or menu item."
  },
  "aria-input-field-name": {
    t: "Custom input fields have no name",
    w: "People using screen readers don't know what to enter.",
    f: "add a label to each custom input field."
  },
  "aria-toggle-field-name": {
    t: "Toggles and checkboxes have no name",
    w: "People using screen readers don't know what they switch.",
    f: "add a label to each toggle or checkbox."
  },
  "aria-tooltip-name": {
    t: "Tooltips have no text",
    w: "Screen readers announce an empty tooltip.",
    f: "add text to each tooltip."
  },
  "aria-dialog-name": {
    t: "Pop-up windows have no name",
    w: "Screen readers can't say what the pop-up is for.",
    f: "add a title or label to each pop-up window."
  },
  "aria-progressbar-name": {
    t: "Progress bars have no name",
    w: "Screen readers can't say what is loading.",
    f: "add a label to each progress bar."
  },
  "aria-meter-name": {
    t: "Meters have no name",
    w: "Screen readers can't say what the meter measures.",
    f: "add a label to each meter."
  },
  "aria-text": {
    t: "Text groups contain clickable items",
    w: "Screen readers may skip the clickable items.",
    f: "keep links and buttons out of text-only groups."
  }
};
const ADA_PREFIX = [["landmark-", {
  t: "Page areas are set up incorrectly",
  w: "People who jump between page areas (header, main, footer) get lost.",
  f: "use one header, one main area, and one footer per page, each at the top level."
}], ["aria-", {
  t: "Accessibility settings are used incorrectly",
  w: "Screen readers may describe parts of the page wrongly.",
  f: "check the accessibility settings on these elements against their purpose."
}]];

// Plain-language names for SEO checks (DataForSEO On-Page check ids).
const SEO_RULES = {
  no_title: {
    t: "Pages have no title",
    w: "Search engines show something random as the headline in results.",
    f: "give each page a title of 30 to 60 characters that says what it offers."
  },
  title_too_short: {
    t: "Page titles are too short",
    w: "Short titles give searchers little reason to click.",
    f: "write titles of 30 to 60 characters that say what the page offers.",
    big: "page titles that are too short to tell searchers what the page is about"
  },
  title_too_long: {
    t: "Page titles are too long",
    w: "Search results cut them off partway.",
    f: "keep titles under 60 characters with the important words first."
  },
  duplicate_title: {
    t: "Several pages share the same title",
    w: "Search engines can't tell which page to show.",
    f: "give each page its own title."
  },
  no_description: {
    t: "Pages have no summary for search results",
    w: "Search engines pick a random snippet to show under your link.",
    f: "write a one or two sentence summary (meta description) for each page."
  },
  duplicate_description: {
    t: "Several pages share the same summary",
    w: "Searchers can't tell the pages apart in results.",
    f: "write a different summary for each page."
  },
  irrelevant_description: {
    t: "Page summaries don't match the page",
    w: "Search engines may ignore the summary and searchers may bounce.",
    f: "rewrite the summary to describe what the page actually shows."
  },
  no_h1_tag: {
    t: "Pages have no main heading",
    w: "Search engines rely on it to understand the page's topic.",
    f: "add one main heading near the top of each page."
  },
  duplicate_content: {
    t: "Pages repeat the same content",
    w: "Search engines may show only one of them, or neither.",
    f: "combine the pages or make each one distinct."
  },
  low_content_rate: {
    t: "Pages have very little text",
    w: "Search engines struggle to tell what thin pages are about.",
    f: "add a few short paragraphs describing the page.",
    big: "pages with very little text"
  },
  no_image_alt: {
    t: "Images have no description",
    w: "Search engines can't see what images show.",
    f: "add a short description (alt text) to each image."
  },
  seo_friendly_url: {
    t: "Page addresses are hard to read",
    w: "Long or cryptic addresses rank and get clicked less.",
    f: "use short addresses with real words separated by dashes."
  },
  no_canonical: {
    t: "Pages don't say which version is the main one",
    w: "Search engines may split ranking between copies of the same page.",
    f: "add a canonical tag pointing to the main version of each page."
  },
  canonical_chain: {
    t: "Main-version tags point through other pages",
    w: "Search engines may give up following the chain.",
    f: "point each canonical tag straight at the final page."
  },
  canonical_to_broken: {
    t: "Main-version tags point to broken pages",
    w: "Search engines may drop the page from results.",
    f: "point each canonical tag at a page that works."
  },
  is_orphan_page: {
    t: "Pages nothing links to",
    w: "Search engines and visitors have a hard time finding them.",
    f: "link to these pages from related pages or your menu."
  },
  is_broken: {
    t: "Pages are broken",
    w: "Visitors and search engines hit a dead end.",
    f: "fix the page or redirect it to a working one."
  },
  is_4xx_code: {
    t: "Pages show a “not found” type error",
    w: "Visitors hit a dead end and search engines drop the page.",
    f: "restore the page or redirect it to the closest working page."
  },
  is_5xx_code: {
    t: "Pages show a server error",
    w: "Visitors can't load the page and search engines lose trust in the site.",
    f: "ask whoever runs your site to find and fix the server error."
  },
  links_broken: {
    t: "Pages link to broken pages",
    w: "Visitors hit dead ends and search engines see a poorly kept site.",
    f: "update or remove links that lead to missing pages."
  },
  broken_links: {
    t: "Pages link to broken pages",
    w: "Visitors hit dead ends and search engines see a poorly kept site.",
    f: "update or remove links that lead to missing pages."
  },
  broken_resources: {
    t: "Pages load files that are missing",
    w: "Images, styles, or scripts fail, so the page may look or work wrong.",
    f: "fix or remove references to missing files."
  },
  is_redirect: {
    t: "Links go through a redirect",
    w: "Each redirect slows the visit a little.",
    f: "update links to point straight at the final page."
  },
  redirect_chain: {
    t: "Pages redirect more than once",
    w: "Chains slow pages down and search engines may stop following them.",
    f: "redirect straight to the final page in one step."
  },
  high_loading_time: {
    t: "Pages load slowly",
    w: "Visitors leave and search engines rank slow pages lower.",
    f: "shrink large images and remove scripts the page doesn't need."
  },
  slow_page: {
    t: "Pages load slowly",
    w: "Visitors leave and search engines rank slow pages lower.",
    f: "shrink large images and remove scripts the page doesn't need."
  },
  large_page_size: {
    t: "Pages are very large",
    w: "Big pages load slowly, especially on phones.",
    f: "compress images and trim unused code."
  },
  no_doctype: {
    t: "Pages are missing a basic setup line",
    w: "Browsers may display the page inconsistently.",
    f: "add <!DOCTYPE html> at the very top of each page's template."
  },
  irrelevant_meta_keywords: {
    t: "Keyword tags don't match the page",
    w: "They add nothing and can look like spam.",
    f: "remove the keywords tag or make it match the page."
  }
};
function describe(kind, issue) {
  const id = String(issue.ruleId || "");
  let hit = (kind === "ada" ? ADA_RULES : SEO_RULES)[id];
  if (!hit && kind === "ada") {
    const p = ADA_PREFIX.find(([pre]) => id.startsWith(pre));
    if (p) hit = p[1];
  }
  if (hit) return {
    title: hit.t,
    why: hit.w,
    fix: hit.f,
    big: hit.big || null
  };
  let d = {};
  try {
    d = JSON.parse(issue.detailJson || "{}") || {};
  } catch (e) {
    d = {};
  }
  return {
    title: d.help || humanize(id),
    why: d.description || null,
    fix: issue.fixSuggestion || null,
    big: null
  };
}
const SUBTITLE = {
  ada: "Things on your site that make it hard to use for people with disabilities",
  seo: "Things on your site that hold back how search engines read and rank it"
};
const TABS = [["fix", "To fix"], ["ok", "Marked as OK"], ["fixed", "Fixed"]];
function tabOf(status) {
  const s = String(status || "").toLowerCase();
  return s === "accepted" ? "ok" : s === "fixed" ? "fixed" : "fix";
}
const POLL_MS = 15000;
const POLL_MAX = 60; // about 15 minutes

class SiteMonitorViewComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "selectSite", siteId => {
      this.stopPolling();
      this.setState({
        siteId,
        summary: null,
        issues: null,
        scans: [],
        running: false,
        expanded: {},
        topic: "",
        notice: null
      });
      this.loadSite(siteId, true);
    });
    _defineProperty(this, "checkNow", () => {
      const {
        siteId,
        running,
        scans
      } = this.state;
      if (!siteId || running) return;
      const baseline = scans && scans[0] ? scans[0].id : null;
      this.setState({
        running: true,
        notice: null
      });
      svcRoot(this.kind(), `/sites/${siteId}/run`).post({}, err => {
        if (!this._alive) return;
        if (err) {
          this.setState({
            running: false,
            notice: "We couldn't start the check. Try again in a minute."
          });
          return;
        }
        this.setState({
          notice: "Checking your site now. This usually takes a minute or two, and results show up here when it's done."
        });
        this.startPolling(siteId, baseline);
      });
    });
    _defineProperty(this, "addSite", () => {
      const {
        addUrl,
        addLabel,
        adding
      } = this.state;
      if (!addUrl.trim() || adding) return;
      const url = /^https?:/i.test(addUrl.trim()) ? addUrl.trim() : `https://${addUrl.trim()}`;
      this.setState({
        adding: true
      });
      svcRoot(this.kind(), "/sites").post({
        url,
        label: addLabel.trim() || null
      }, (err, site) => {
        if (!this._alive) return;
        if (err) {
          this.setState({
            adding: false,
            notice: "We couldn't add that site. Check the address and try again."
          });
          return;
        }
        this.setState({
          adding: false,
          showSettings: false,
          addUrl: "",
          addLabel: "",
          notice: "Site added. Press Check now to run the first check, or wait for the daily check."
        });
        this.loadSites(site && site.id);
      });
    });
    // Accept (mark as OK) or unaccept a set of issues, one request each.
    _defineProperty(this, "setOk", async (issues, accept, busyKey) => {
      if (this.state.busy || !issues.length) return;
      const siteId = this.state.siteId;
      this.setState({
        busy: busyKey
      });
      let failed = 0;
      for (const issue of issues) {
        const {
          err
        } = await postAsync(this.kind(), `/sites/${siteId}/issues/${issue.issueHash}/${accept ? "accept" : "unaccept"}`);
        if (!this._alive || this.state.siteId !== siteId) return;
        if (err) {
          failed++;
          continue;
        }
        this.setState(s => ({
          issues: (s.issues || []).map(i => i.issueHash === issue.issueHash ? {
            ...i,
            status: accept ? "accepted" : "open"
          } : i)
        }));
      }
      this.setState({
        busy: null,
        notice: failed ? `${plural(failed, "page")} couldn't be updated. Try again.` : null
      });
      safe(svcRoot(this.kind(), `/sites/${siteId}/summary`).getAsync(), null).then(summary => {
        if (this._alive && summary && this.state.siteId === siteId) this.setState({
          summary
        });
      });
    });
    _defineProperty(this, "toggle", key => this.setState(s => ({
      expanded: {
        ...s.expanded,
        [key]: !s.expanded[key]
      }
    })));
    _defineProperty(this, "renderGroup", g => {
      const {
        tab,
        expanded,
        busy
      } = this.state;
      const open = !!expanded[g.key];
      const sev = String(g.severity || "").toLowerCase();
      const n = g.pageList.length;
      const groupBusy = busy === `g:${g.key}`;
      const action = tab === "fix" ? {
        label: "Mark as OK",
        accept: true
      } : tab === "ok" ? {
        label: "Back to fix",
        accept: false
      } : null;
      return /*#__PURE__*/React.createElement("div", {
        className: `ack-row${open ? " open" : ""}`,
        key: g.key
      }, /*#__PURE__*/React.createElement("div", {
        className: "ack-row-main"
      }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("span", {
        className: `ack-sev ${SEV_TONE[sev] || "blue"}`
      }, sev ? sev[0].toUpperCase() + sev.slice(1) : "Info")), /*#__PURE__*/React.createElement("div", {
        className: "ack-txt"
      }, /*#__PURE__*/React.createElement("b", null, g.desc.title, g.hasNew && tab === "fix" ? /*#__PURE__*/React.createElement("span", {
        className: "ack-new"
      }, "New") : null), g.desc.why ? /*#__PURE__*/React.createElement("span", {
        className: "ack-why"
      }, g.desc.why) : null, g.desc.fix ? /*#__PURE__*/React.createElement("span", {
        className: "ack-fix"
      }, "How to fix: ", g.desc.fix) : null), /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "ack-pages-link",
        "aria-expanded": open,
        onClick: () => this.toggle(g.key)
      }, "On ", plural(n, "page"), " ", /*#__PURE__*/React.createElement("span", {
        className: "ack-caret"
      }, open ? "▴" : "▾")), /*#__PURE__*/React.createElement("span", {
        className: "ack-act"
      }, action ? /*#__PURE__*/React.createElement(Button, {
        size: "small",
        disabled: !!busy,
        onClick: () => this.setOk(g.issues, action.accept, `g:${g.key}`),
        title: action.accept ? "Stop counting this as a problem on every page it was found" : "Count this as a problem again"
      }, groupBusy ? "Saving…" : action.label) : null)), open ? /*#__PURE__*/React.createElement("div", {
        className: "ack-pages"
      }, g.pageList.map(p => {
        const pBusy = busy === `p:${g.key}:${p.key}`;
        return /*#__PURE__*/React.createElement("div", {
          className: "ack-page",
          key: p.key
        }, /*#__PURE__*/React.createElement("a", {
          href: p.url,
          target: "_blank",
          rel: "noreferrer",
          title: p.url
        }, p.key.replace(/^https?:\/\/(www\.)?/i, "") || p.url), /*#__PURE__*/React.createElement("span", {
          className: "ack-first"
        }, p.first ? `First seen ${fmtDate(p.first)}` : ""), /*#__PURE__*/React.createElement("span", {
          className: "ack-act"
        }, action ? /*#__PURE__*/React.createElement("button", {
          type: "button",
          className: "ack-link",
          disabled: !!busy,
          onClick: () => this.setOk(p.issues, action.accept, `p:${g.key}:${p.key}`)
        }, pBusy ? "Saving…" : action.label) : null));
      }), /*#__PURE__*/React.createElement("div", {
        className: "ack-details"
      }, "Details: ", g.ruleId, g.criterion ? ` · ${String(g.criterion).split(",").map(x => x.trim()).filter(Boolean).join(" · ")}` : "", g.helpUrl ? /*#__PURE__*/React.createElement(React.Fragment, null, " ", "·", " ", /*#__PURE__*/React.createElement("a", {
        href: g.helpUrl,
        target: "_blank",
        rel: "noreferrer"
      }, "Learn more")) : null)) : null);
    });
    this.state = {
      sites: null,
      siteId: null,
      summary: null,
      scans: [],
      issues: null,
      error: false,
      tab: "fix",
      topic: "",
      expanded: {},
      showSettings: false,
      addUrl: "",
      addLabel: "",
      adding: false,
      running: false,
      busy: null,
      notice: null,
      showHistory: false
    };
    this._alive = true;
    this._poll = null;
  }
  kind() {
    return this.props.kind === "ada" ? "ada" : "seo";
  }
  title() {
    return this.kind() === "ada" ? "Accessibility checks" : "SEO checks";
  }
  async componentDidMount() {
    if (Auth.isAuthenticated() || (await Auth.attachOrSignIn())) this.loadSites();
  }
  componentDidUpdate(prev) {
    if (prev.kind !== this.props.kind) {
      this.stopPolling();
      this.setState({
        sites: null,
        siteId: null,
        summary: null,
        scans: [],
        issues: null,
        tab: "fix",
        topic: "",
        expanded: {},
        running: false,
        notice: null
      });
      this.loadSites();
    }
  }
  componentWillUnmount() {
    this._alive = false;
    this.stopPolling();
  }
  async loadSites(preferId) {
    try {
      const sites = ((await svcRoot(this.kind(), "/sites").getAsync()) || []).filter(s => s.status !== "Inactive");
      if (!this._alive) return;
      this.setState({
        sites
      });
      const keep = preferId && sites.find(s => s.id === preferId) ? preferId : this.state.siteId && sites.find(s => s.id === this.state.siteId) ? this.state.siteId : sites.length ? sites[0].id : null;
      if (keep) this.selectSite(keep);
    } catch (e) {
      if (this._alive) this.setState({
        error: true
      });
    }
  }
  async loadSite(siteId, maybeResumePoll) {
    const k = this.kind();
    const [summary, scans, issues] = await Promise.all([safe(svcRoot(k, `/sites/${siteId}/summary`).getAsync(), null), safe(svcRoot(k, `/sites/${siteId}/scans?$top=15`).getAsync(), []), safe(svcRoot(k, `/sites/${siteId}/issues?$top=500`).getAsync(), [])]);
    if (!this._alive || this.state.siteId !== siteId) return;
    this.setState({
      summary,
      scans,
      issues
    });
    // A check that started in the last hour and hasn't finished: keep watching it.
    if (maybeResumePoll && scans.length && !scans[0].finishedDate && scans[0].success == null) {
      const started = toDate(scans[0].startedDate);
      if (started && Date.now() - started.getTime() < 3600 * 1000) this.startPolling(siteId, null);
    }
  }

  // ---- polling while a check runs ----
  startPolling(siteId, baselineScanId) {
    this.stopPolling();
    this.setState({
      running: true
    });
    let ticks = 0;
    this._poll = setInterval(async () => {
      ticks++;
      if (!this._alive || this.state.siteId !== siteId) {
        this.stopPolling();
        return;
      }
      const scans = await safe(svcRoot(this.kind(), `/sites/${siteId}/scans?$top=15`).getAsync(), null);
      if (!this._alive || this.state.siteId !== siteId) return;
      const top = scans && scans[0];
      const finished = top && (top.finishedDate || top.success != null) && (baselineScanId == null || top.id !== baselineScanId);
      if (finished) {
        this.stopPolling();
        this.setState({
          running: false,
          notice: top.success === false ? "The check didn't finish. Try again in a few minutes." : null
        });
        this.loadSite(siteId, false);
      } else if (ticks >= POLL_MAX) {
        this.stopPolling();
        this.setState({
          running: false,
          notice: "The check is taking longer than usual. Results will show here when it's done."
        });
      }
    }, POLL_MS);
  }
  stopPolling() {
    if (this._poll) {
      clearInterval(this._poll);
      this._poll = null;
    }
  }
  // Group issues (already filtered to one tab) by check, with pages normalized.
  groupIssues(list) {
    const kind = this.kind();
    const map = new Map();
    for (const i of list) {
      const key = String(i.ruleId || "other");
      let g = map.get(key);
      if (!g) {
        g = {
          key,
          ruleId: i.ruleId,
          criterion: i.criterion,
          severity: i.severity,
          desc: describe(kind, i),
          issues: [],
          pages: new Map(),
          hasNew: false,
          helpUrl: null
        };
        map.set(key, g);
      }
      g.issues.push(i);
      if (sevRank(i.severity) < sevRank(g.severity)) g.severity = i.severity;
      if (String(i.status).toLowerCase() === "new") g.hasNew = true;
      if (!g.helpUrl) {
        try {
          g.helpUrl = (JSON.parse(i.detailJson || "{}") || {}).helpUrl || null;
        } catch (e) {/* no detail */}
      }
      const pk = pageKey(i.page);
      let p = g.pages.get(pk);
      if (!p) {
        p = {
          key: pk,
          url: i.page,
          first: i.createdDate,
          issues: []
        };
        g.pages.set(pk, p);
      }
      p.issues.push(i);
      const a = toDate(i.createdDate),
        b = toDate(p.first);
      if (a && (!b || a < b)) p.first = i.createdDate;
    }
    return [...map.values()].map(g => ({
      ...g,
      pageList: [...g.pages.values()].sort((a, b) => a.key.localeCompare(b.key))
    })).sort((a, b) => sevRank(a.severity) - sevRank(b.severity) || b.pageList.length - a.pageList.length || a.desc.title.localeCompare(b.desc.title));
  }
  renderSettings(sites, siteId) {
    const {
      addUrl,
      addLabel,
      adding
    } = this.state;
    return /*#__PURE__*/React.createElement("div", {
      className: "ack-modal-bg",
      onClick: () => this.setState({
        showSettings: false
      })
    }, /*#__PURE__*/React.createElement("div", {
      className: "ack-modal",
      role: "dialog",
      "aria-label": "Settings",
      onClick: e => e.stopPropagation()
    }, /*#__PURE__*/React.createElement("div", {
      className: "ack-modal-hd"
    }, /*#__PURE__*/React.createElement("b", null, "Settings"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ack-x",
      "aria-label": "Close",
      onClick: () => this.setState({
        showSettings: false
      })
    }, "\xD7")), sites.length > 0 && /*#__PURE__*/React.createElement("div", {
      className: "ack-set-sec"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ack-set-l"
    }, "Site to check"), /*#__PURE__*/React.createElement("div", {
      className: "ack-sites"
    }, sites.map(s => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: s.id,
      className: `ack-site${s.id === siteId ? " on" : ""}`,
      onClick: () => {
        this.setState({
          showSettings: false
        });
        if (s.id !== siteId) this.selectSite(s.id);
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: "ack-radio"
    }), /*#__PURE__*/React.createElement("span", {
      className: "ack-site-t"
    }, /*#__PURE__*/React.createElement("b", null, s.label || siteName(s)), /*#__PURE__*/React.createElement("span", null, s.url)))))), /*#__PURE__*/React.createElement("div", {
      className: "ack-set-sec"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ack-set-l"
    }, sites.length ? "Add another site" : "Add your site"), this.renderAddForm(addUrl, addLabel, adding), /*#__PURE__*/React.createElement("div", {
      className: "ack-hint"
    }, "We check up to 50 pages once a day. Press Check now any time for a fresh check."))));
  }
  renderAddForm(addUrl, addLabel, adding) {
    return /*#__PURE__*/React.createElement("form", {
      className: "ack-add",
      onSubmit: e => {
        e.preventDefault();
        this.addSite();
      }
    }, /*#__PURE__*/React.createElement("input", {
      className: "ack-in ack-in-url",
      placeholder: "www.yoursite.com",
      value: addUrl,
      onChange: e => this.setState({
        addUrl: e.target.value
      })
    }), /*#__PURE__*/React.createElement("input", {
      className: "ack-in",
      placeholder: "Name (optional)",
      value: addLabel,
      onChange: e => this.setState({
        addLabel: e.target.value
      })
    }), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      type: "submit",
      disabled: !addUrl.trim() || adding
    }, adding ? "Adding…" : "Add site"));
  }
  renderHistory(scans) {
    return /*#__PURE__*/React.createElement("section", {
      className: "ack-card ack-history"
    }, /*#__PURE__*/React.createElement("b", {
      className: "ack-h"
    }, "Recent checks"), /*#__PURE__*/React.createElement("div", {
      className: "ack-tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "ack-tbl"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "When"), /*#__PURE__*/React.createElement("th", null, "How"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Pages"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "New"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Fixed"), /*#__PURE__*/React.createElement("th", null, "Result"))), /*#__PURE__*/React.createElement("tbody", null, scans.map(sc => /*#__PURE__*/React.createElement("tr", {
      key: sc.id
    }, /*#__PURE__*/React.createElement("td", null, fmtWhen(sc.startedDate)), /*#__PURE__*/React.createElement("td", null, sc.trigger === "manual" ? "Check now" : sc.trigger === "daily" ? "Daily" : sc.trigger || ""), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, sc.pagesScanned ?? ""), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, sc.newIssues ?? ""), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, sc.fixedIssues ?? ""), /*#__PURE__*/React.createElement("td", {
      className: "ack-res",
      title: sc.message || ""
    }, sc.success === true ? "Done" : sc.success === false ? `Didn't finish${sc.message ? `: ${sc.message}` : ""}` : "Running")))))));
  }
  render() {
    const {
      sites,
      siteId,
      summary,
      scans,
      issues,
      error,
      tab,
      topic,
      showSettings,
      running,
      notice,
      showHistory
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    if (sites === null) return /*#__PURE__*/React.createElement(Loader, null);
    const kind = this.kind();
    const site = sites.find(s => s.id === siteId);
    const ls = summary && summary.latestScan || scans && scans.find(s => s.finishedDate || s.success != null) || null;
    const all = issues || [];
    const byTab = {
      fix: [],
      ok: [],
      fixed: []
    };
    for (const i of all) byTab[tabOf(i.status)].push(i);
    const groups = {
      fix: this.groupIssues(byTab.fix),
      ok: this.groupIssues(byTab.ok),
      fixed: this.groupIssues(byTab.fixed)
    };
    const openPages = new Set(byTab.fix.map(i => pageKey(i.page))).size;
    const newCount = groups.fix.filter(g => g.hasNew).length;
    const topics = kind === "seo" ? [...new Set(groups[tab].map(g => g.criterion).filter(Boolean))].sort() : [];
    const shown = groups[tab].filter(g => !topic || g.criterion === topic);

    // Verdict
    const checkedLine = ls ? `Checked ${siteName(site)} ${fmtDay(ls.finishedDate || ls.startedDate)}, ${plural(ls.pagesScanned ?? 0, "page")}.` : "";
    let tone = "good",
      head,
      sub;
    if (!ls && !all.length) {
      tone = "idle";
      head = running ? "Running the first check" : "No checks yet";
      sub = running ? "This usually takes a minute or two." : "Press Check now to run the first one, or wait for the daily check.";
    } else if (groups.fix.length === 0) {
      head = "Nothing to fix.";
      sub = [groups.fixed.length ? `${plural(groups.fixed.length, "earlier problem")} ${groups.fixed.length === 1 ? "is" : "are"} fixed.` : "", checkedLine].filter(Boolean).join(" ");
    } else {
      const top = groups.fix[0];
      tone = sevRank(top.severity) <= 1 ? "bad" : "warn";
      head = `${plural(groups.fix.length, "problem")} to fix, on ${plural(openPages, "page")}`;
      const bigText = top.desc.big || top.desc.title.toLowerCase();
      const biggest = groups.fix.length === 1 ? `It's about ${bigText}.` : `The biggest is ${bigText}.`;
      sub = [biggest, groups.fixed.length ? `${plural(groups.fixed.length, "earlier problem")} ${groups.fixed.length === 1 ? "is" : "are"} fixed.` : "", checkedLine].filter(Boolean).join(" ");
    }
    const failedLast = scans && scans[0] && scans[0].success === false;
    return /*#__PURE__*/React.createElement("div", {
      className: "wa-view ack-view"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ack-hdr"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ack-hdr-t"
    }, /*#__PURE__*/React.createElement("span", {
      className: "ack-crumb"
    }, /*#__PURE__*/React.createElement(Link, {
      to: "/Apps"
    }, "Apps"), " ", "·", " ", this.title()), /*#__PURE__*/React.createElement("h1", null, this.title()), /*#__PURE__*/React.createElement("span", {
      className: "ack-sub"
    }, SUBTITLE[kind])), /*#__PURE__*/React.createElement("div", {
      className: "ack-hdr-a"
    }, /*#__PURE__*/React.createElement(Button, {
      onClick: () => this.setState({
        showSettings: true
      })
    }, "Settings"), sites.length > 0 && /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: running || !siteId,
      onClick: this.checkNow
    }, running ? "Checking…" : "Check now"))), notice && /*#__PURE__*/React.createElement("div", {
      className: "ack-notice"
    }, /*#__PURE__*/React.createElement("span", null, notice), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ack-link",
      onClick: () => this.setState({
        notice: null
      })
    }, "Dismiss")), sites.length === 0 ? /*#__PURE__*/React.createElement("section", {
      className: "ack-card ack-empty"
    }, /*#__PURE__*/React.createElement("b", null, "Add your site to start checking"), /*#__PURE__*/React.createElement("span", {
      className: "ack-why"
    }, "We check up to 50 public pages once a day and list anything that needs fixing."), this.renderAddForm(this.state.addUrl, this.state.addLabel, this.state.adding)) : issues === null ? /*#__PURE__*/React.createElement(Loader, null) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("section", {
      className: "ack-card ack-verdict"
    }, /*#__PURE__*/React.createElement("span", {
      className: `ack-dot ${tone}`
    }), /*#__PURE__*/React.createElement("div", {
      className: "ack-verdict-t"
    }, /*#__PURE__*/React.createElement("b", null, head), /*#__PURE__*/React.createElement("span", null, sub), failedLast ? /*#__PURE__*/React.createElement("span", {
      className: "ack-warn"
    }, "The last check didn't finish", scans[0].message ? ` (${scans[0].message})` : "", ". Press Check now to try again.") : null)), /*#__PURE__*/React.createElement(Ribbon, {
      comparing: null,
      cols: 4,
      tiles: [{
        l: "Problems to fix",
        v: String(groups.fix.length),
        s: `on ${plural(openPages, "page")}`
      }, {
        l: "New since last check",
        v: String(newCount)
      }, {
        l: "Marked as OK",
        v: String(groups.ok.length)
      }, {
        l: "Fixed",
        v: String(groups.fixed.length),
        s: groups.fixed.length ? "since you started" : ""
      }]
    }), /*#__PURE__*/React.createElement("section", {
      className: "ack-card ack-list"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ack-pills"
    }, TABS.map(([k, l]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: k,
      className: `ack-pill${tab === k ? " on" : ""}`,
      onClick: () => this.setState({
        tab: k,
        topic: "",
        expanded: {}
      })
    }, l, " ", groups[k].length)), /*#__PURE__*/React.createElement("span", {
      className: "ack-grow"
    }), topics.length > 1 && /*#__PURE__*/React.createElement("select", {
      className: "ack-pill ack-topic",
      value: topic,
      onChange: e => this.setState({
        topic: e.target.value
      }),
      "aria-label": "Topic"
    }, /*#__PURE__*/React.createElement("option", {
      value: ""
    }, "All topics"), topics.map(t => /*#__PURE__*/React.createElement("option", {
      key: t,
      value: t
    }, t)))), shown.length === 0 ? /*#__PURE__*/React.createElement("div", {
      className: "ack-none"
    }, tab === "fix" ? "Nothing to fix right now." : tab === "ok" ? "Nothing is marked as OK. Use Mark as OK on a problem you've decided to leave as is." : "Nothing fixed yet. Problems move here once a check no longer finds them.") : shown.map(this.renderGroup)), /*#__PURE__*/React.createElement("div", {
      className: "ack-foot"
    }, /*#__PURE__*/React.createElement("span", null, "We only check your public website pages. Pages that differ only by tracking tags (like ?utm=) count once."), scans.length > 0 && /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ack-link",
      onClick: () => this.setState({
        showHistory: !showHistory
      })
    }, showHistory ? "Hide recent checks" : "See recent checks")), showHistory && scans.length > 0 ? this.renderHistory(scans) : null), showSettings ? this.renderSettings(sites, siteId) : null);
  }
}
export default withRouter(SiteMonitorViewComponent);